'use client';

import { useTranslation } from 'next-i18next';

import { useMemo } from 'react';

import { Badge, Box, BoxProps, Button, Skeleton } from '@chakra-ui/react';

import { ScrollableWrapper } from '@nocowanie/common-ui/components/molecules/scrollable-wrapper';
import { IconsLinear } from '@nocowanie/icons/linear';

import { MAX_VISIBLE_FILTERS } from '@app/consts';
import { useActiveFilters } from '@app/hooks/useActiveFilters';
import { useListingSearch } from '@app/hooks/useListingSearch';

const commonScrollableContainerProps = {
    alignItems: 'center',
    m: 0,
    py: {
        base: 1,
        md: 4,
    },
    gap: 2,
    scrollSnapType: 'none',
    gridAutoColumns: 'max-content',
    w: 'max-content',
};

const commonProps = {
    borderRadius: 'full',
    px: 3,
    height: 8,
};

export const ListingActiveFilters = ({
    showClearAllButton = true,
    withScrollableWrapper = false,
    ...rest
}: BoxProps & {
    showClearAllButton?: boolean;
    withScrollableWrapper?: boolean;
}) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'filters',
    });
    const { isMobileRes, isSearchLoading, isInitialRender } = useListingSearch();
    const { activeFilters, handleClearAllFilters, handleRemoveFilter } = useActiveFilters();

    const renderBadges = useMemo(() => {
        const maxVisibleFilters = isMobileRes ? MAX_VISIBLE_FILTERS : undefined;

        const badges = activeFilters.slice(0, maxVisibleFilters).map(filter => (
            <Skeleton
                asChild
                loading={isSearchLoading || isInitialRender}
                key={`${filter.key}-${filter.value}`}
            >
                <Badge
                    variant={'outline'}
                    as={'button'}
                    colorPalette={'gray'}
                    fontWeight={'normal'}
                    onClick={() => handleRemoveFilter(filter)}
                    cursor={'pointer'}
                    {...commonProps}
                >
                    {filter.label}
                    <IconsLinear.Close size="sm" color={'gray.600'} />
                </Badge>
            </Skeleton>
        ));

        if (showClearAllButton && activeFilters.length) {
            badges.push(
                <Skeleton
                    asChild
                    loading={isSearchLoading || isInitialRender}
                    key={'clear-all'}
                    onClick={handleClearAllFilters}
                >
                    <Button
                        variant={'link'}
                        size={'sm'}
                        {...commonProps}
                        css={{
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                            textDecorationColor: 'currentColor/20',
                            transition: isSearchLoading || isInitialRender ? 'none' : undefined,
                        }}
                    >
                        {t('clearAll')}
                    </Button>
                </Skeleton>,
            );
        }

        return badges;
    }, [
        activeFilters,
        handleClearAllFilters,
        handleRemoveFilter,
        isInitialRender,
        isMobileRes,
        isSearchLoading,
        showClearAllButton,
        t,
    ]);

    return (
        <Box
            display="flex"
            alignItems={'center'}
            gap={2}
            data-testid={'active-filters'}
            flexWrap={withScrollableWrapper ? undefined : 'wrap'}
            {...rest}
        >
            {withScrollableWrapper ? (
                <ScrollableWrapper
                    scrollableContainerProps={{
                        ...commonScrollableContainerProps,
                        pl: 0,
                        maxW: '100%',
                    }}
                    showArrows
                    loading={isSearchLoading}
                >
                    {renderBadges}
                </ScrollableWrapper>
            ) : (
                renderBadges
            )}
        </Box>
    );
};
